import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, Circle, X } from "lucide-react";
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Switch,
  FormControl,
  Select,
  MenuItem,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";

const AddResourceSchedulerAccount = (Props) => {
  const [step, setStep] = useState(1);
  let accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [region, setRegion] = useState([]);
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#003366",
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            color: "#4B5563",
            fontWeight: 500,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            color: "#003366",
            backgroundColor: "#F9FAFB",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            color: "#003366",
            "&:hover": {
              backgroundColor: "rgba(0, 51, 102, 0.08)",
            },
            "&.Mui-selected": {
              backgroundColor: "rgba(0, 51, 102, 0.12)",
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .MuiSwitch-track": {
              backgroundColor: "#003366",
            },
            "& .MuiSwitch-thumb": {
              backgroundColor: "#003366",
            },
          },
        },
      },
    },
  });

  const [formData, setFormData] = useState({
    accountName: "",
    accountNumber: "",
    region: "",
    portalUrl: "",
    apiUrl: "",
    username: "",
    password: "",
    days: ["2", "3", "4", "5", "6"],
    timezone: "Asia/Calcutta",
    startTime: "07:00",
    endTime: "23:00",
    startEnabled: false,
    stopEnabled: false,
  });

  const steps = [
    { id: 1, title: "Account & API Details" },
    { id: 2, title: "Schedule Settings" },
    { id: 3, title: "Review" },
  ];

  const dayOptions = [
    { value: "2", label: "Mon" },
    { value: "3", label: "Tue" },
    { value: "4", label: "Wed" },
    { value: "5", label: "Thu" },
    { value: "6", label: "Fri" },
    { value: "7", label: "Sat" },
    { value: "1", label: "Sun" },
  ];

  const timezoneOptions = [
    { value: "Asia/Calcutta", label: "Asia/Calcutta" },
    { value: "Etc/GMT-4", label: "Etc/GMT-4" },
   { value: "SystemV/CST6", label: "SystemV/CST6" },
  ];

  function convertToObjectsArray(data) {
    return Object.keys(data).map((key) => ({
      name: key,
      description: data[key],
    }));
  }

  const getData = async () => {
    const query = `
    query GetDetails($id: String!) {
      getDetails(id: $id) {
        region
      }
    }`;

    const variables = { id: "1" };
    const response1 = await axios.post(
      apiUrl,
      { query, variables },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    console.log(response1)
    if (response1.data.data && response1.data.data.getDetails) {
      const configurationDetails = JSON.parse(response1.request.response);
      setRegion(
        convertToObjectsArray(
          JSON.parse(configurationDetails.data.getDetails.region)
        )
      );
    }

    const queryAws = `
      query MyQuery {
        getAWSAccounts {
          AccountName
          AccountNo
          id
        }
      }
    `;

    await axios
      .post(
        apiUrl,
        { query: queryAws },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (response.data.data && response.data.data.getAWSAccounts)
          setAwsAccounts(response.data.data.getAWSAccounts);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleDayToggle = (dayValue) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(dayValue)
        ? prev.days.filter((d) => d !== dayValue)
        : [...prev.days, dayValue],
    }));
  };

  const validateStep = () => {
    const newErrors = {};
    switch (step) {
      case 1:
        if (!formData.accountName)
          newErrors.accountName = "Account name is required";
        if (!formData.accountNumber)
          newErrors.accountNumber = "Account number is required";
        if (!formData.region) newErrors.region = "Region is required";
        if (!formData.portalUrl) newErrors.portalUrl = "Portal URL is required";
        if (!formData.apiUrl) newErrors.apiUrl = "API URL is required";
        if (!formData.username) newErrors.username = "Username is required";
        if (!formData.password) newErrors.password = "Password is required";
        break;
      case 2:
        if (!formData.timezone) newErrors.timezone = "Please select a timezone";
        if (formData.startEnabled && !formData.startTime)
          newErrors.startTime = "Start time is required";
        if (formData.stopEnabled && !formData.endTime)
          newErrors.endTime = "End time is required";
        if (formData.days.length === 0)
          newErrors.days = "Please select at least one day";
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  function generateCronExpressions(days, startTime, endTime) {
    const parseTime = (time) => {
      const [hours, minutes] = time.split(":").map(Number);
      return { hours, minutes };
    };
    const subtract30Minutes = (hours, minutes) => {
      const totalMinutes = hours * 60 + minutes - 30;
      const adjustedHours = Math.floor(((totalMinutes + 1440) % 1440) / 60);
      const adjustedMinutes = ((totalMinutes + 1440) % 1440) % 60;
      return { hours: adjustedHours, minutes: adjustedMinutes };
    };
    const { hours: startHour, minutes: startMinute } = parseTime(startTime);
    const { hours: endHour, minutes: endMinute } = parseTime(endTime);
    const { hours: halfHourBeforeHour, minutes: halfHourBeforeMinute } =
      subtract30Minutes(startHour, startMinute);

    const daysString = days.join(",");
    const hasWeekend = days.some((day) => day == "1" || day == "7");
    const endCronDays = hasWeekend ? daysString : `${daysString},1,7`;

    const startCron = `${startMinute} ${startHour} ? * ${daysString} *`;
    let endCron = `${endMinute} ${endHour} ? * ${endCronDays} *`;
    const halfHourBeforeCron = `${halfHourBeforeMinute} ${halfHourBeforeHour} ? * ${daysString} *`;
    return {
      startCron,
      endCron,
      halfHourBeforeCron,
    };
  }

  const postData = async () => {
    const query = `
    mutation MyMutation($AccountName:String ,$AccountNo:String ,$apiUrl:String , $password:String ,$portalUrl:String ,$region:String ,$scheduledescription:String ,$startecscronexpression:String ,$startrdscronexpression:String,$stateStart:String!,$stateStop:String! ,$stopcronexpression:String ,$timezone:String ,$username:String, $startTime:String, $stopTime:String, $days:[String] ) {
    postAccounts(
    AccountName: $AccountName
    AccountNo: $AccountNo
    apiUrl: $apiUrl
    password: $password
    portalUrl: $portalUrl
    region:$region
    stateStart: $stateStart
    stateStop: $stateStop
    scheduledescription: $scheduledescription
    startecscronexpression: $startecscronexpression
    startrdscronexpression: $startrdscronexpression
    stopcronexpression: $stopcronexpression
    timezone: $timezone
    username: $username
    startTime:$startTime
    stopTime: $stopTime
    days:$days
  )
}`;
    const { startCron, halfHourBeforeCron } = formData.startEnabled
      ? generateCronExpressions(
          formData.days,
          formData.startTime,
          formData.endTime
        )
      : {
          startCron: "00 7 ? * 2,3,4,5,6 *",
          halfHourBeforeCron: "30 6 ? * 2,3,4,5,6 *",
        };
    const { endCron } = formData.stopEnabled
      ? generateCronExpressions(
          formData.days,
          formData.startTime,
          formData.endTime
        )
      : {
          endCron: "23 00 ? * 2,3,4,5,6 *",
        };

    const description = generateScheduleDescription(
      formData.days,
      formData.startTime,
      formData.endTime,
      formData.startEnabled,
      formData.stopEnabled
    );
    const variables = {
      AccountName: formData.accountName,
      AccountNo: formData.accountNumber,
      apiUrl: formData.apiUrl,
      password: formData.password,
      portalUrl: formData.portalUrl,
      region: formData.region,
      scheduledescription: description,
      startecscronexpression: startCron,
      stateStart: formData.startEnabled ? "ENABLED" : "DISABLED",
      stateStop: formData.stopEnabled ? "ENABLED" : "DISABLED",
      startrdscronexpression: halfHourBeforeCron,
      stopcronexpression: endCron,
      timezone: formData.timezone,
      username: formData.username,
      days: formData.days,
      startTime: formData.startTime,
      stopTime: formData.endTime,
    };

    await axios
      .post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        )
          setError("You do not have permission to Add an Account");
        Props.setRefresh(!Props.refresh);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleClose = () => {
    Props.onClose();
    setFormData({
      accountName: "",
      accountNumber: "",
      region: "",
      portalUrl: "",
      apiUrl: "",
      username: "",
      password: "",
      days: ["2", "3", "4", "5", "6"],
      timezone: "Asia/Calcutta",
      startTime: "07:00",
      endTime: "23:00",
      startEnabled: false,
      stopEnabled: false,
    });
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setIsSubmitting(true);
    try {
      await postData();
      setShowSuccess(true);
      setTimeout(() => {
        Props.onClose();
        setTimeout(() => {
          setShowSuccess(false);
          setFormData({
            accountName: "",
            accountNumber: "",
            region: "",
            portalUrl: "",
            apiUrl: "",
            username: "",
            password: "",
            days: ["2", "3", "4", "5", "6"],
            timezone: "Asia/Calcutta",
            startTime: "07:00",
            endTime: "23:00",
            startEnabled: false,
            stopEnabled: false,
          });
          setStep(1);
        }, 100);
      }, 2000);
    } catch(err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  function generateScheduleDescription(
    days,
    startTime,
    endTime,
    startEnabled,
    stopEnabled
  ) {
    if (!startEnabled && !stopEnabled) {
      return "Disabled";
    }
    if ((startEnabled && !stopEnabled) || (!startEnabled && stopEnabled)) {
      return "On demand";
    }
    const numberToDays = {
      0: "Sunday",
      1: "Monday",
      2: "Tuesday",
      3: "Wednesday",
      4: "Thursday",
      5: "Friday",
      6: "Saturday",
    };
    const weekdays = [1, 2, 3, 4, 5];
    const weekend = [0, 6];
    const zeroIndexedDays = days.map((day) => day - 1);
    const isWeekend =
      zeroIndexedDays.length === 2 &&
      zeroIndexedDays.every((day) => weekend.includes(day)) &&
      zeroIndexedDays.includes(0) &&
      zeroIndexedDays.includes(6);
    const isWeekday =
      zeroIndexedDays.length === 5 &&
      zeroIndexedDays.every((day) => weekdays.includes(day));
    let consecutive = true;
    for (let i = 0; i < zeroIndexedDays.length - 1; i++) {
      if (zeroIndexedDays[i + 1] - zeroIndexedDays[i] !== 1) {
        consecutive = false;
        break;
      }
    }
    const timeDescription = `${startTime} to ${endTime}`;
    if (isWeekend) {
      return `Weekend ${timeDescription}`;
    }
    if (isWeekday) {
      return `Weekdays ${timeDescription}`;
    }
    if (consecutive && zeroIndexedDays.length > 1) {
      return `${numberToDays[zeroIndexedDays[0]]}-${
        numberToDays[zeroIndexedDays[zeroIndexedDays.length - 1]]
      } ${timeDescription}`;
    }
    if (zeroIndexedDays.length === 1) {
      return `${numberToDays[zeroIndexedDays[0]]} ${timeDescription}`;
    }
    const dayNames = zeroIndexedDays.map((day) => numberToDays[day]);
    return `${dayNames.join(", ")} ${timeDescription}`;
  }
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Account Name
                </label>
                <select
                  value={formData.accountName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      accountName: e.target.value,
                    }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                >
                  <option value="">Select Account Name</option>
                  {awsAccounts?.map((option) => (
                    <option key={option.AccountName} value={option.AccountName}>
                      {option.AccountName}
                    </option>
                  ))}
                </select>
                {errors.accountName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.accountName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Account Number
                </label>
                <select
                  value={formData.accountNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      accountNumber: e.target.value,
                    }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                >
                  <option value="">Select Account Number</option>
                  {awsAccounts?.map((option) => (
                    <option key={option.AccountNo} value={option.AccountNo}>
                      {option.AccountNo}
                    </option>
                  ))}
                </select>
                {errors.accountNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.accountNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Region
                </label>
                <select
                  value={formData.region}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, region: e.target.value }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                >
                  <option value="">Select Region</option>
                  {region?.map((option) => (
                    <option key={option.name} value={option.name}>
                      {option.description}
                    </option>
                  ))}
                </select>
                {errors.region && (
                  <p className="text-red-500 text-sm mt-1">{errors.region}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Portal URL
                </label>
                <input
                  type="text"
                  value={formData.portalUrl}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      portalUrl: e.target.value,
                    }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                />
                {errors.portalUrl && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.portalUrl}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  API URL
                </label>
                <input
                  type="text"
                  value={formData.apiUrl}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, apiUrl: e.target.value }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                />
                {errors.apiUrl && (
                  <p className="text-red-500 text-sm mt-1">{errors.apiUrl}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                />
                {errors.username && (
                  <p className="text-red-500 text-sm mt-1">{errors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-opacity-50 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <ThemeProvider theme={theme}>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Time Configuration
                </h3>
                <div className="space-y-4">
                  <div className="p-3 border rounded-lg bg-gray-50">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.timezone}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            timezone: e.target.value,
                          }))
                        }
                        className="bg-white"
                      >
                        {timezoneOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors.timezone && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.timezone}
                      </p>
                    )}
                  </div>

                  <div className="p-3 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium text-gray-900">
                          Start Time
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          When resources should start
                        </p>
                      </div>
                      <Switch
                        checked={formData.startEnabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            startEnabled: e.target.checked,
                          }))
                        }
                      />
                    </div>
                    {formData.startEnabled && (
                      <input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            startTime: e.target.value,
                          }))
                        }
                        className="w-full p-2 border rounded-lg bg-white focus:ring-2 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                      />
                    )}
                    {errors.startTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.startTime}
                      </p>
                    )}
                  </div>

                  <div className="p-3 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium text-gray-900">
                          Stop Time
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          When resources should stop
                        </p>
                      </div>
                      <Switch
                        checked={formData.stopEnabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            stopEnabled: e.target.checked,
                          }))
                        }
                      />
                    </div>
                    {formData.stopEnabled && (
                      <input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            endTime: e.target.value,
                          }))
                        }
                        className="w-full p-2 border rounded-lg bg-white focus:ring-2 focus:ring-[#003366] focus:border-[#003366] transition-colors"
                      />
                    )}
                    {errors.endTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.endTime}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Operating Days
                </h3>
                <div className="p-3 border rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-600 mb-3">
                    Select the days when resources should be active
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {dayOptions.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => handleDayToggle(day.value)}
                        className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors
                            ${
                              formData.days.includes(day.value)
                                ? "bg-[#003366] text-white"
                                : "bg-white text-gray-700 hover:bg-gray-100 border"
                            }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {errors.days && (
                    <p className="text-red-500 text-sm mt-1">{errors.days}</p>
                  )}

                  <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-gray-600">
                      Schedule will run based on selected days and times in the
                      chosen timezone. The stop schedule is enabled on weekends
                      by default unless weekend days are specifically selected.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ThemeProvider>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border rounded-lg bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Account Details
                </h3>
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Account Name
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.accountName}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Account Number
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.accountNumber}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Region
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.region}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Portal URL
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.portalUrl}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      API URL
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.apiUrl}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6 border rounded-lg bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Schedule Details
                </h3>
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Timezone
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.timezone}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Start Time
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.startEnabled ? formData.startTime : "Disabled"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      End Time
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.stopEnabled ? formData.endTime : "Disabled"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium text-gray-500 min-w-[120px]">
                      Selected Days
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {formData.days
                        .map(
                          (d) =>
                            dayOptions.find((opt) => opt.value === d)?.label
                        )
                        .join(", ")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-span-1 md:col-span-2 p-6 border rounded-lg bg-blue-50">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Schedule Summary
                </h3>
                <p className="text-sm text-gray-700 break-words">
                  {generateScheduleDescription(
                    formData.days,
                    formData.startTime,
                    formData.endTime,
                    formData.startEnabled,
                    formData.stopEnabled
                  )}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={Props.open}
      maxWidth={showSuccess ? "sm" : "md"}
      fullWidth={!showSuccess}
    >
      <DialogContent className="p-0">
        {showSuccess ? (
          <div className="w-full py-3">
            <div className="text-center">
              {error ? (
                <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-1" />
              ) : (
                <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-1" />
              )}

              <h2 className="text-lg font-semibold text-gray-900">
                {error
                  ? "You do not have permission to add a new account"
                  : "Account Added Successfully!"}
              </h2>
            </div>
          </div>
        ) : (
          <Card>
            <div className="p-4 border-b flex justify-between items-center bg-[#003366]/5 shrink-0">
              <h2 className="text-2xl font-bold text-[#003366]">Add Account</h2>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <CardContent>
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  {steps.map((s) => (
                    <div key={s.id} className="flex items-center">
                      {s.id < step && (
                        <CheckCircle className="h-8 w-8 text-[#003366]" />
                      )}
                      {s.id === step && (
                        <div className="h-8 w-8 rounded-full border-2 border-[#003366] text-[#003366] flex items-center justify-center">
                          {s.id}
                        </div>
                      )}
                      {s.id > step && (
                        <Circle className="h-8 w-8 text-gray-300" />
                      )}
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {s.title}
                      </span>
                      {s.id !== steps.length && (
                        <div className="mx-4 h-0.5 w-16 bg-gray-200"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                {renderStepContent()}

                <div className="mt-8 flex justify-between">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Back
                    </button>
                  )}
                  {step < steps.length ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="ml-auto px-4 py-2 text-sm font-medium text-white bg-[#003366] rounded-lg hover:bg-[#002244] transition-colors"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="ml-auto px-4 py-2 text-sm font-medium text-white bg-[#003366] rounded-lg hover:bg-[#002244] transition-colors"
                    >
                      {isSubmitting ? "Adding..." : "Add Account"}
                    </button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddResourceSchedulerAccount;
